import { errorReporter } from './errorReporter';

/**
 * localStorage is not always usable, and the ways it fails are browser-specific:
 * Safari with all cookies blocked throws SecurityError, Firefox with
 * cookies blocked reports a refused write as QuotaExceededError, and
 * private modes or a full disk throw QuotaExceededError for real. None of it is
 * recoverable and none of it is a bug in the app, so every access fails soft here
 * rather than throwing out of a render.
 */
const getStore = (): Storage | null => {
  try {
    // Typed as always present, but the accessor itself throws where storage is denied.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return window.localStorage ?? null;
  } catch {
    return null;
  }
};

// Read structurally: DOMException — which is what every one of these failures is — does
// not reliably inherit from Error across engines, so `instanceof` would lose the name
// that distinguishes a denied write (SecurityError) from a full one (QuotaExceededError).
const errorName = (err: unknown): string =>
  typeof err === 'object' && err !== null && 'name' in err && typeof err.name === 'string' ? err.name : 'unknown';

// A breadcrumb, not a report: storage being denied is a property of the visitor's
// browser settings, so reporting it would only recreate the noise this replaces. The
// crumb still lands on any later error from the same session.
const noteFailure = (operation: string, key: string, err: unknown): void => {
  errorReporter.breadcrumb({
    category: 'localStorage',
    message: `${operation} refused for "${key}"`,
    level: 'warning',
    data: { error: errorName(err) },
  });
};

export const safeLocalStorage = {
  /** Whether this browsing context allows localStorage at all. */
  isAvailable(): boolean {
    return getStore() !== null;
  },

  /** The stored value, or null when absent *or* unreadable — callers treat both alike. */
  getItem(key: string): string | null {
    const store = getStore();
    if (!store) return null;

    try {
      return store.getItem(key);
    } catch (err) {
      noteFailure('read', key, err);
      return null;
    }
  },

  /** @returns whether the value actually persisted. */
  setItem(key: string, value: string): boolean {
    const store = getStore();
    if (!store) return false;

    try {
      store.setItem(key, value);
      return true;
    } catch (err) {
      noteFailure('write', key, err);
      return false;
    }
  },

  removeItem(key: string): void {
    const store = getStore();
    if (!store) return;

    try {
      store.removeItem(key);
    } catch (err) {
      noteFailure('delete', key, err);
    }
  },
};
